import { asHTML, asText } from '@prismicio/client'
import type { Ref } from 'vue'

type Row = { item?: string | null }

type VacancyData = {
  title?: unknown
  introduction?: unknown
  what_you_will_do?: Row[]
  what_we_search?: Row[]
  employment_conditions?: Row[]
  employment_type?: string | null
  hours_per_week?: string | null
  location?: string | null
  work_area?: string | null
  salary_range?: string | null
  seo_description?: string | null
}

type VacancyDoc =
  | { data?: VacancyData; first_publication_date?: string | null }
  | null
  | undefined

const EMPLOYMENT_TYPE_MAP: Record<string, string | string[]> = {
  Fulltime: 'FULL_TIME',
  Parttime: 'PART_TIME',
  'Fulltime / Parttime': ['FULL_TIME', 'PART_TIME'],
  Stage: 'INTERN',
  Bijbaan: 'PART_TIME',
  'ZZP / Freelance': 'CONTRACTOR',
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function listHtml(rows: Row[] | undefined, ordered: boolean): string {
  if (!rows?.length) return ''
  const items = rows
    .map((r) => (r.item ? `<li>${escapeHtml(r.item)}</li>` : ''))
    .join('')
  if (!items) return ''
  return ordered ? `<ol>${items}</ol>` : `<ul>${items}</ul>`
}

/**
 * Parse a free-text salary like "€ 2.900 – € 4.100 p/m" into a
 * schema.org MonetaryAmount. Returns undefined when it can't be parsed.
 */
function parseSalary(raw: string | null | undefined) {
  if (!raw) return undefined
  const nums = (raw.match(/\d[\d.]*/g) || [])
    .map((n) => Number(n.replace(/\./g, '')))
    .filter((n) => Number.isFinite(n) && n > 0)
  if (!nums.length) return undefined

  const lower = raw.toLowerCase()
  const unitText = /uur/.test(lower)
    ? 'HOUR'
    : /jaar/.test(lower)
      ? 'YEAR'
      : /week/.test(lower)
        ? 'WEEK'
        : 'MONTH'

  const value =
    nums.length >= 2
      ? { '@type': 'QuantitativeValue', minValue: nums[0], maxValue: nums[1], unitText }
      : { '@type': 'QuantitativeValue', value: nums[0], unitText }

  return {
    '@type': 'MonetaryAmount',
    currency: 'EUR',
    value,
  }
}

function toIsoDate(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined
  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString()
}

/**
 * Emit schema.org JobPosting structured data (JSON-LD) for a vacancy so it is
 * eligible for Google Jobs rich results.
 * https://developers.google.com/search/docs/appearance/structured-data/job-posting
 */
export function useJobPostingSchema(doc: Ref<VacancyDoc>) {
  const settings = useSettings()
  const url = useRequestURL()

  const schema = computed(() => {
    const d = doc.value?.data
    if (!d) return null

    const title = asText(d.title)
    if (!title) return null

    const orgName = asText(settings.value?.data?.siteTitle) || 'Pro-Air'
    const origin = `${url.protocol}//${url.host}`

    const descriptionHtml =
      [
        asHTML(d.introduction) || '',
        d.what_you_will_do?.length ? '<h3>Wat ga je doen?</h3>' + listHtml(d.what_you_will_do, true) : '',
        d.what_we_search?.length ? '<h3>Wat vragen wij</h3>' + listHtml(d.what_we_search, false) : '',
        d.employment_conditions?.length ? '<h3>Wat bieden wij</h3>' + listHtml(d.employment_conditions, false) : '',
      ]
        .filter(Boolean)
        .join('') ||
      (d.seo_description ? escapeHtml(d.seo_description) : escapeHtml(title))

    const employmentType = d.employment_type ? EMPLOYMENT_TYPE_MAP[d.employment_type] : undefined
    const baseSalary = parseSalary(d.salary_range)
    const locality = d.location || d.work_area || undefined
    const datePosted = toIsoDate(doc.value?.first_publication_date)

    const json: Record<string, unknown> = {
      '@context': 'https://schema.org/',
      '@type': 'JobPosting',
      title,
      description: descriptionHtml,
      directApply: true,
      hiringOrganization: {
        '@type': 'Organization',
        name: orgName,
        sameAs: origin,
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          ...(locality ? { addressLocality: locality } : {}),
          addressCountry: 'NL',
        },
      },
      identifier: {
        '@type': 'PropertyValue',
        name: orgName,
        value: url.pathname,
      },
    }

    if (datePosted) json.datePosted = datePosted
    if (employmentType) json.employmentType = employmentType
    if (baseSalary) json.baseSalary = baseSalary

    return json
  })

  useHead(() => ({
    script: schema.value
      ? [
          {
            type: 'application/ld+json',
            // Escape `<` so CMS content can never break out of the <script> tag.
            innerHTML: JSON.stringify(schema.value).replace(/</g, '\\u003c'),
          },
        ]
      : [],
  }))
}
